import { useState, useEffect, useContext, useRef } from "react"
import axios from "axios"
import { FileText, Edit, Trash2, LinkIcon, X } from "lucide-react"
import { PlusIcon } from '@heroicons/react/20/solid'

import { AuthContext } from "../../../utils/auth.js";
import { ref, get } from "firebase/database";
import { db } from '../../../utils/firebaseApp.js';
import toast from 'react-hot-toast';
import { DocTypeNameCombo } from './DocTypeNameCombo';

export function EditClassificationModal({ open, onOpenChange, currentClassificationData, classificationLinkingDocType, setClassificationLinkingDocType }) {
    const [name, setName] = useState("")
    const [description, setDescription] = useState("")
    const [documentTypes, setDocumentTypes] = useState([])
    const { currentUser } = useContext(AuthContext);

    const [showDocTypeForm, setShowDocTypeForm] = useState(false)
    const [editingDocType, setEditingDocType] = useState(null)
    const [docTypeName, setDocTypeName] = useState("")
    const [docTypeDescription, setDocTypeDescription] = useState("")
    const [uniqueWord, setUniqueWord] = useState("")
    const [uniqueWords, setUniqueWords] = useState([])

    const [showTemplateLinking, setShowTemplateLinking] = useState(false)
    const [linkingDocType, setLinkingDocType] = useState(null)
    const [selectedTemplate, setSelectedTemplate] = useState(null)
    const [searchQuery, setSearchQuery] = useState("")

    //get all extractions for the current user
    const [extractionTemplates, setExtractionTemplates] = useState([])
    //define useref for doctypename and uniquewords
    const docTypeNameRef = useRef();
    const docUniqueWordsRef = useRef();
    const docTypeDescriptionRef = useRef();
    const classificationNameRef = useRef();

    const fetchExtractionNames = async () => {
        const userId = currentUser.uid;
        const extractionsRef = ref(db, `extractions/${userId}`);
        const snapshot = await get(extractionsRef);
        if (snapshot.exists()) {
            const extractionsData = snapshot.val();
            const tempExtractions = Object.keys(extractionsData).map((extractionId) => {
                const extraction = extractionsData[extractionId];
                return {
                    id: extractionId,
                    name: extraction.name,
                    fields: extraction.fields || {},
                }
            })
            setExtractionTemplates(tempExtractions);
        }
    }

    useEffect(() => {
        fetchExtractionNames();
    }, [currentUser.uid]);


    useEffect(() => {
        if (currentClassificationData) {
            setName(currentClassificationData.name || "")
            setDescription(currentClassificationData.description || "")
            if (currentClassificationData.documentTypes) {
                setDocumentTypes(prev => {
                    const alreadyInitialized = prev.length > 0 && prev.every(p => p.id);
                    if (alreadyInitialized) return prev; // skip re-init

                    const updated = currentClassificationData.documentTypes.map(docType => ({
                        ...docType,
                        id: docType.id || generateDocTypeId()
                    }));

                    return updated;
                });
            } else {
                setDocumentTypes([]);
            }
        } else {
            resetForm()
        }

    }, [currentClassificationData, open])

    const triedLinkingRef = useRef(false);

    // Reset flag when classificationLinkingDocType changes
    useEffect(() => {
        if (classificationLinkingDocType) {
            triedLinkingRef.current = false;
        }
    }, [classificationLinkingDocType]);

    useEffect(() => {
        if (triedLinkingRef.current || !classificationLinkingDocType || documentTypes.length === 0) return;

        const docType = documentTypes.find((dt) =>
            dt.name === classificationLinkingDocType.name &&
            dt.description === classificationLinkingDocType.description &&
            JSON.stringify(dt.uniqueWords) === JSON.stringify(classificationLinkingDocType.uniqueWords) &&
            dt.extractionId === classificationLinkingDocType.extractionId
        );

        if (docType) {
            triedLinkingRef.current = true;
            setClassificationLinkingDocType(null);
            handleLinkTemplate(docType);
        }
    }, [classificationLinkingDocType, documentTypes]);

    const submitData = async () => {
        const serverUrl = process.env.REACT_APP_SERVER_URL;
        const environment = process.env.REACT_APP_ENV;

        if (currentClassificationData) {
            // Update existing classification
            // TODO: Implement update classification
            const updatedClassification = {
                name: name,
                description: description,
                documentTypes: documentTypes ?? {},
            }

            await currentUser.getIdToken().then(async (idToken) => {
                const url = serverUrl + "/documentClassification/updateClassification";

                await axios.patch(url, {
                    "classificationId": currentClassificationData.id,
                    "classificationDetails": updatedClassification,
                }, {
                    headers: {
                        "Content-Type": "application/json",
                        "Access-Control-Allow-Origin": "*",
                        Authorization: `Bearer ${idToken}`
                    }
                }).then((response) => {
                    if (response.status === 200) {
                        toast.success("Classification updated successfully");
                        setTimeout(() => {
                            window.location.reload();
                        }, 1000);
                    } else {
                        toast.error("Classification update failed");
                    }
                }).catch((error) => {
                    toast.error("Classification update failed");
                    console.log(error.response.data);
                });
            });

        } else {
            const newClassification = {
                name: name,
                description: description,
                documentTypes: documentTypes ?? {},
            }

            await currentUser.getIdToken().then(async (idToken) => {
                const url = serverUrl + "/documentClassification/createClassification";

                await axios.post(url, {
                    "classificationDetails": newClassification,
                }, {
                    headers: {
                        "Content-Type": "application/json",
                        "Access-Control-Allow-Origin": "*",
                        Authorization: `Bearer ${idToken}`
                    }
                }).then((response) => {
                    if (response.status === 200) {
                        toast.success("Classification created successfully");
                        setTimeout(() => {
                            //relocate to /classifications/classificationId
                            window.location.href = "/classifications/" + response.data.classificationId;
                        }, 100);
                    } else {
                        toast.error("Classification creation failed");
                    }
                }).catch((error) => {
                    toast.error("Classification creation failed");
                });
            });
        }

    }

    const resetForm = () => {
        setName("")
        setDescription("")
        setDocumentTypes([])
        setLinkingDocType(null) //edge case for individual doc linking
        setShowTemplateLinking(false) //edge case for individual doc linking
        resetDocTypeForm()
    }

    const resetDocTypeForm = () => {
        setDocTypeName("")
        setDocTypeDescription("")
        setUniqueWord("")
        setUniqueWords([])
        setEditingDocType(null)
    }

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!classificationNameRef.current.value) {
            classificationNameRef.current.setCustomValidity("Classification name is required");
            classificationNameRef.current.reportValidity();
            return;
        } else {
            classificationNameRef.current.setCustomValidity("");
        }

        if (documentTypes.length === 0) {
            //open add doc type form
            setShowDocTypeForm(true)
            return;
        }

        submitData();
        onOpenChange(false);
        resetForm();
    }

    const handleAddWord = () => {
        if (uniqueWord.trim() && !uniqueWords.includes(uniqueWord.trim())) {
            setUniqueWords([...uniqueWords, uniqueWord.trim()])
            setUniqueWord("")
        }
    }

    const handleRemoveWord = (word) => {
        setUniqueWords(uniqueWords.filter((w) => w !== word))
    }

    const handleAddDocType = () => {
        setShowDocTypeForm(true)
        resetDocTypeForm()
    }

    const handleEditDocType = (docType) => {
        setEditingDocType(docType)
        setDocTypeName(docType.name)
        setDocTypeDescription(docType.description || "")
        setUniqueWords(docType.uniqueWords || [])
        setShowDocTypeForm(true)
    }

    const handleDeleteDocType = (docTypeId) => {
        setDocumentTypes(documentTypes.filter((dt) => dt.id !== docTypeId))
    }

    const generateDocTypeId = () => {
        const id = Date.now().toString(36) + Math.random().toString(36).substring(2, 15);
        return id;
    }

    const handleSaveDocType = () => {
        //if (!docTypeName.trim() || uniqueWords.length === 0) return

        //check if docTypeName is empty
        if (!docTypeNameRef.current.value) {
            docTypeNameRef.current.setCustomValidity("Document type name is required");
            docTypeNameRef.current.reportValidity();
            return;
        } else if (docTypeNameRef.current.value === 'other') {
            docTypeNameRef.current.setCustomValidity("Document type name cannot be 'other'");
            docTypeNameRef.current.reportValidity();
            return;
        } else if (documentTypes.some(dt => dt.name === docTypeNameRef.current.value &&
            (!editingDocType || dt.id !== editingDocType.id))) {
            docTypeNameRef.current.setCustomValidity("Document type name must be unique");
            docTypeNameRef.current.reportValidity();
            return;
        } else {
            docTypeNameRef.current.setCustomValidity(""); // CLEAR custom error
        }

        // Check if docTypeDescription is empty
        if (!docTypeDescriptionRef.current.value) {
            docTypeDescriptionRef.current.setCustomValidity("Document type description is required");
            docTypeDescriptionRef.current.reportValidity();
            return;
        } else {
            docTypeDescriptionRef.current.setCustomValidity(""); // CLEAR custom error
        }

        // Check if uniqueWords is empty
        if (uniqueWords.length === 0) {
            docUniqueWordsRef.current.setCustomValidity("At least one unique word is required");
            docUniqueWordsRef.current.reportValidity();
            return;
        } else {
            docUniqueWordsRef.current.setCustomValidity(""); // CLEAR custom error
        }


        const newDocType = {
            id: editingDocType ? editingDocType.id : generateDocTypeId(),
            name: docTypeName,
            description: docTypeDescription,
            uniqueWords: uniqueWords,
            //linkedTemplate: editingDocType?.linkedTemplate || null,
            //templateId: editingDocType?.templateId || null,
            extractionId: editingDocType?.extractionId || null,
        }

        if (editingDocType) {
            setDocumentTypes(documentTypes.map((dt) => (dt.id === editingDocType.id ? newDocType : dt)))
        } else {
            setDocumentTypes([...documentTypes, newDocType])
        }

        setShowDocTypeForm(false)
        resetDocTypeForm()
    }

    const handleLinkTemplate = (docType) => {
        setLinkingDocType(docType)
        setSelectedTemplate(docType.extractionId || null)
        setShowTemplateLinking(true)
        setSearchQuery("")
    }

    const findTemplateName = (templateId) => {
        const template = extractionTemplates.find((t) => t.id === templateId)
        return template?.name || null
    }

    const handleSaveTemplateLink = () => {
        if (!linkingDocType || !selectedTemplate) return


        const template = extractionTemplates.find((t) => t.id === selectedTemplate)

        setDocumentTypes(prevTypes =>
            prevTypes.map((dt) =>
                dt.id === linkingDocType.id
                    ? {
                        ...dt,
                        extractionId: template?.id || null,
                        //templateId: selectedTemplate,
                    }
                    : dt,
            ),
        )

        setShowTemplateLinking(false)
        //setLinkingDocType(null) //edge case for individual doc linking
        setSelectedTemplate(null)
    }

    const filteredTemplates = extractionTemplates.filter((template) =>
        template.name.toLowerCase().includes(searchQuery.toLowerCase()),
    )

    return (
        open && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
                <div className="bg-white rounded-lg shadow-lg sm:max-w-[800px] max-h-[90vh] overflow-hidden flex flex-col w-full mx-4">
                    {/* Header */}
                    <div className="p-6 pb-0">
                        <h3 className="text-base font-semibold leading-6 text-gray-900">
                            {currentClassificationData ? "Edit Classification" : "Create New Classification"}
                        </h3>
                        <p className="text-sm text-gray-500 mt-1 max-w-xl">
                            {currentClassificationData
                                ? "Edit classification details and manage document outputs"
                                : "Create a new document classification and add document outputs"}
                        </p>
                    </div>
                    {/* Content slot here */}
                    <div className="flex-1 p-6 overflow-y-auto max-h-[calc(90vh-150px)]">
                        <div className="grid gap-2">
                            {/* Classification Details */}
                            <div className="grid gap-4">
                                <div className="grid gap-2">
                                    <label htmlFor="name" className="block text-sm font-medium leading-6 text-gray-900">
                                        Classification Name
                                    </label>
                                    <input
                                        ref={classificationNameRef}
                                        id="name"
                                        value={name}
                                        onInput={() => classificationNameRef.current.setCustomValidity("")}
                                        onChange={(e) => setName(e.target.value)}
                                        placeholder="e.g., Invoice Processing"
                                        required
                                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                    />
                                </div>

                                <div className="grid gap-2">
                                    <label htmlFor="description" className="block text-sm font-medium leading-6 text-gray-900">
                                        Description
                                        <span className='ml-1 text-xs font-normal text-gray-400'>
                                            {"(optional)"}
                                        </span>
                                    </label>
                                    <textarea
                                        id="description"
                                        value={description}
                                        onChange={(e) => setDescription(e.target.value)}
                                        placeholder="Describe the purpose of this classification"
                                        rows={3}
                                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                    />
                                </div>
                            </div>

                            <div className="border-t my-4" />

                            {/* Document Types */}
                            <div>
                                <div className="flex items-start justify-between mb-4">
                                    <div className="flex-1 mr-4">
                                        <h3 className="text-base font-semibold leading-6 text-gray-900">
                                            Document Classification Outputs
                                        </h3>
                                        <p className="text-sm text-gray-500">
                                            Define possible document outputs and link them to data extraction templates.
                                        </p>
                                    </div>
                                    <button
                                        onClick={handleAddDocType}
                                        className="flex-shrink-0 inline-flex items-center gap-x-1.5 rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                                    >
                                        <PlusIcon className="-ml-0.5 h-5 w-5" aria-hidden="true" />
                                        Add Document Type
                                    </button>
                                </div>

                                {documentTypes.length === 0 ? (
                                    <div className="text-center py-8 text-gray-500">
                                        No document outputs defined yet.
                                    </div>
                                ) : (
                                    <div className="grid gap-4">
                                        {documentTypes.map((docType) => (
                                            <div key={docType.id} className="border rounded-md shadow-sm">
                                                <div className="p-4">
                                                    <div className="flex items-center justify-between mb-2">
                                                        <div className="flex items-center">
                                                            <FileText className="mr-2 h-5 w-5 text-gray-400" />
                                                            <h3 className="font-semibold">{docType.name}</h3>
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            <button
                                                                onClick={() => handleEditDocType(docType)}
                                                                className="flex items-center px-2 py-1 text-sm border rounded-md hover:bg-gray-100"
                                                            >
                                                                <Edit className="mr-2 h-4 w-4" />
                                                                Edit
                                                            </button>
                                                            <button
                                                                onClick={() => handleDeleteDocType(docType.id)}
                                                                className="px-2 py-1.5 border rounded-md text-red-600 hover:bg-red-50 flex items-center justify-center"
                                                            >
                                                                <Trash2 className="h-4 w-4" />
                                                            </button>
                                                        </div>
                                                    </div>

                                                    {docType.description && (
                                                        <p className="text-sm text-gray-500 mb-3">{docType.description}</p>
                                                    )}

                                                    <div className="flex flex-col gap-3">
                                                        <div>
                                                            <h4 className="text-xs font-medium mb-1.5">Unique Words/Phrases</h4>
                                                            <div className="flex flex-wrap gap-1.5">
                                                                {docType.uniqueWords.map((word, index) => (
                                                                    <span
                                                                        key={index}
                                                                        className="text-xs bg-gray-100 border rounded px-2 py-0.5"
                                                                    >
                                                                        {word}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        </div>

                                                        <div>
                                                            <div className="flex items-center justify-between mb-1.5">
                                                                <h4 className="text-xs font-medium">Extraction Template</h4>
                                                                <button
                                                                    onClick={() => handleLinkTemplate(docType)}
                                                                    className="h-7 px-2 py-1 text-xs border rounded hover:bg-gray-100 flex items-center"
                                                                >
                                                                    <LinkIcon className="mr-1 h-3 w-3" />
                                                                    {docType.extractionId ? "Change Template" : "Link Template"}
                                                                </button>
                                                            </div>
                                                            {docType.extractionId ? (
                                                                <div className="p-2 bg-green-50 rounded-md border border-green-100">
                                                                    <div className="flex items-center text-green-700">
                                                                        <FileText className="h-3.5 w-3.5 mr-1.5" />
                                                                        <span className="font-medium text-sm">{findTemplateName(docType.extractionId)}</span>
                                                                    </div>
                                                                </div>
                                                            ) : (
                                                                <div className="p-2 py-3 bg-gray-100 rounded-md border border-gray-200">
                                                                    <p className="text-xs text-gray-500">No template linked</p>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Document Type Form Dialog */}
                    {showDocTypeForm && (
                        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
                            <div className="bg-white rounded-lg shadow-lg w-full max-w-[600px] mx-4">
                                {/* Header */}
                                <div className="p-6 pb-0">
                                    <h3 className="text-base font-semibold leading-6 text-gray-900">
                                        {editingDocType ? "Edit Document Type" : "Add Document Type"}
                                    </h3>
                                    <p className="max-w-xl text-sm text-gray-500">
                                        Define a document type that the system should find during classification.
                                    </p>
                                </div>
                                {/* Body */}
                                <div className="p-6 space-y-4">
                                    <div className="space-y-2">
                                        <label htmlFor="docTypeName" className="block text-sm font-medium leading-6 text-gray-900">
                                            Document Type Name
                                        </label>
                                        <DocTypeNameCombo
                                            docTypeName={docTypeName}
                                            docTypeNameRef={docTypeNameRef}
                                            setDocTypeName={setDocTypeName}
                                            setDocTypeDescription={setDocTypeDescription}
                                            setUniqueWords={setUniqueWords}
                                        />
                                    </div>

                                    <div className="space-y-2">
                                        <label htmlFor="docTypeDescription" className="block text-sm font-medium leading-6 text-gray-900">
                                            Description
                                        </label>
                                        <textarea
                                            ref={docTypeDescriptionRef}
                                            id="docTypeDescription"
                                            value={docTypeDescription}
                                            onInput={() => docTypeDescriptionRef.current.setCustomValidity("")}
                                            onChange={(e) => setDocTypeDescription(e.target.value)}
                                            placeholder="Add details about this document type"
                                            rows={2}
                                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                        />
                                    </div>

                                    <div className="space-y-2">
                                        <label className="block text-sm font-medium leading-6 text-gray-900">
                                            Unique Words/Phrases
                                        </label>
                                        <div className="flex">
                                            <input
                                                ref={docUniqueWordsRef}
                                                value={uniqueWord}
                                                onInput={() => docUniqueWordsRef.current.setCustomValidity("")}
                                                onChange={(e) => setUniqueWord(e.target.value)}
                                                onKeyDown={(e) => {
                                                    if (e.key === "Enter") {
                                                        e.preventDefault()
                                                        handleAddWord()
                                                    }
                                                }}
                                                required
                                                placeholder="Add words that identify this document type"
                                                className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                            />
                                            <button
                                                type="button"
                                                onClick={handleAddWord}
                                                className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 sm:ml-2 sm:w-auto"
                                            >
                                                Add
                                            </button>
                                        </div>

                                        {uniqueWords.length > 0 && (
                                            <div className="flex flex-wrap gap-2 mt-2">
                                                {uniqueWords.map((word, index) => (
                                                    <span
                                                        key={index}
                                                        className="flex items-center gap-1 text-sm bg-gray-100 border rounded px-2 py-0.5 cursor-pointer"
                                                        onClick={() => handleRemoveWord(word)}
                                                    >
                                                        {word}
                                                        <X
                                                            className="h-3 w-3"
                                                        />
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                        <p className="text-xs text-gray-500">
                                            These words will be used to identify and classify this document type.
                                        </p>
                                    </div>
                                </div>

                                {/* Document Type Form Footer */}
                                <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6 rounded-lg">
                                    <button
                                        type="button"
                                        onClick={handleSaveDocType}
                                        className={'inline-flex w-full justify-center rounded-md  px-3 py-2 text-sm font-semibold text-white shadow-sm  sm:ml-3 sm:w-auto bg-indigo-600 hover:bg-indigo-500'}

                                    >
                                        {editingDocType ? "Save Changes" : "Add Document Type"}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setShowDocTypeForm(false)
                                            setLinkingDocType(null) //edge case for individual doc linking
                                            setShowTemplateLinking(false) //edge case for individual doc linking
                                        }}
                                        className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Template Linking Dialog */}
                    {showTemplateLinking && (
                        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
                            <div className="bg-white rounded-lg shadow-lg w-full max-w-[600px] mx-4 max-h-[90vh] flex flex-col">
                                {/* Header */}
                                <div className="p-6 pb-0">
                                    <h3 className="text-base font-semibold leading-6 text-gray-900">
                                        Link Extraction Template
                                    </h3>
                                    <p className="text-sm text-gray-500 mt-1 max-w-xl">
                                        Link an extraction template to <strong>{linkingDocType?.name}</strong> for automatic data
                                        extraction after classification.
                                    </p>
                                </div>

                                {/* Body */}
                                <div className="p-6 space-y-4 overflow-y-auto">
                                    <div className="relative mb-4">
                                        <input
                                            type="text"
                                            placeholder="Search templates..."
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            className="w-full rounded-md border-0 py-2 px-9 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"

                                        />
                                        <svg
                                            className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400"
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <circle cx="11" cy="11" r="8" />
                                            <path d="m21 21-4.3-4.3" />
                                        </svg>
                                    </div>

                                    <div className="h-[300px] overflow-y-auto pr-2">
                                        {filteredTemplates.length > 0 ? (
                                            <div className="space-y-3">
                                                {filteredTemplates.map((template) => (
                                                    <label
                                                        key={template.id}
                                                        htmlFor={template.id}
                                                        className={`flex items-center space-x-3 border rounded-md p-3 cursor-pointer hover:bg-gray-100 ${selectedTemplate === template.id ? "border-indigo-500" : "border-gray-200"
                                                            }`}
                                                        ref={selectedTemplate === template.id ? (el) => {
                                                            if (el) {
                                                                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                                            }
                                                        } : null}
                                                    >
                                                        <input
                                                            type="radio"
                                                            id={template.id}
                                                            name="template"
                                                            value={template.id}
                                                            checked={selectedTemplate === template.id}
                                                            onChange={() => setSelectedTemplate(template.id)}
                                                            className="form-radio text-indigo-600 focus:ring-0"
                                                        />
                                                        <div className="flex items-center">
                                                            <FileText className="mr-2 h-5 w-5 text-gray-400" />
                                                            <div>
                                                                <div className="font-medium">{template.name}</div>
                                                                <div className="text-sm text-gray-500">
                                                                    {Object.keys(template.fields).length} extraction fields
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </label>
                                                ))}
                                            </div>
                                        ) : (
                                            <div className="text-center py-8 text-gray-500">
                                                No templates found matching your search
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Template Linking Footer */}
                                <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6 rounded-lg">
                                    <button
                                        onClick={handleSaveTemplateLink}
                                        disabled={!selectedTemplate}
                                        className={`inline-flex w-full justify-center rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm sm:ml-3 sm:w-auto ${!selectedTemplate
                                            ? "bg-gray-300 cursor-not-allowed"
                                            : "bg-indigo-600 hover:bg-indigo-500"
                                            }`}
                                    >
                                        Link Template
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setShowTemplateLinking(false)}
                                        className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Footer */}
                    <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                        <button
                            type="button"
                            onClick={handleSubmit}
                            className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 sm:ml-3 sm:w-auto"
                        >
                            {currentClassificationData ? "Save Changes" : "Create"}
                        </button>
                        <button
                            onClick={() => {
                                onOpenChange(false)
                                resetForm()
                            }}
                            className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        )
    )
}